import { useParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import axios from 'axios'
import Header from '../components/shared/Header'
import Footer from '../components/shared/Footer'

const iconMap = {
  code: (
    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
    </svg>
  ),
  cloud: (
    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
    </svg>
  ),
  brain: (
    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  ),
  integration: (
    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
    </svg>
  ),
  default: (
    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
    </svg>
  )
}

export default function ToolDetailPage() {
  const { toolId } = useParams()
  const [tool, setTool] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchTool = async () => {
      try {
        const response = await axios.get(`/api/v1/tools/${toolId}`)
        setTool(response.data)
      } catch (error) {
        console.error('Failed to fetch tool:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchTool()
  }, [toolId])

  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="max-w-container mx-auto px-5 py-8">
          <Header />
          <div className="text-muted-dark text-center py-20">Loading tool...</div>
          <Footer />
        </div>
      </div>
    )
  }

  if (!tool) {
    return (
      <div className="min-h-screen">
        <div className="max-w-container mx-auto px-5 py-8">
          <Header />
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <h1 className="text-2xl mb-4">Tool not found</h1>
              <button 
                onClick={() => window.location.href = '/'}
                className="text-accent-dark hover:underline"
              >
                Return home
              </button>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    )
  }

  const icon = iconMap[tool.icon] || iconMap.default

  return (
    <div 
      className="min-h-screen"
      style={{
        background: 'radial-gradient(900px 520px at 20% -10%, rgba(122, 215, 255, 0.18), transparent 58%)'
      }}
    >
      <div className="max-w-container mx-auto px-5 md:px-8 py-8">
        <Header />

        {/* Tool Header */}
        <header className="mb-6 md:mb-8">
          <div className="flex items-start gap-4 mb-4">
            <div className="text-accent-dark flex-shrink-0">
              {icon}
            </div>
            <div className="flex-1">
              <div className="flex items-baseline gap-3 mb-2 flex-wrap">
                <h1 className="text-3xl md:text-5xl font-semibold leading-tight tracking-tight">
                  {tool.name}
                </h1>
                <span className="font-mono text-xs tracking-wide uppercase text-muted-dark bg-white/[0.02] px-2.5 py-1.5 rounded border border-border-dark">
                  {tool.category}
                </span>
              </div>
              <p className="text-muted-dark leading-relaxed">
                {tool.description}
              </p>
            </div>
          </div>
          
          {tool.url && (
            <a
              href={tool.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-accent-dark text-black font-semibold text-sm sm:text-base rounded-lg hover:shadow-[0_8px_20px_rgba(122,215,255,0.6)] transition-all hover:scale-105"
            >
              <span>Visit {tool.name}</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          )}
        </header>

        {/* Why I Use It */}
        <div className="border-2 border-accent-dark rounded-large bg-accent-muted-dark p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-semibold mb-4">Why I Use It</h2>
          <p className="text-muted-dark leading-relaxed">
            {tool.why_i_use_it}
          </p>
        </div>

        {/* Use Cases */}
        <div className="border border-border-dark rounded-large bg-white/[0.04] p-6 md:p-8 mb-8">
          <h2 className="text-xl md:text-2xl font-semibold mb-4">Use Cases</h2>
          <ul className="space-y-3">
            {tool.use_cases.map((useCase, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="text-accent-dark flex-shrink-0 mt-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
                <span className="text-muted-dark leading-relaxed">{useCase}</span>
              </li>
            ))}
          </ul>
        </div>

        <Footer />
      </div>
    </div>
  )
}

